/**
 * @fileoverview
 * MSTEM-MODIFIED: Phase 1 FND-03 — locale detection rewrite.
 *
 * Per locked decisions D-11 (ignore browser-language auto-detect) and D-12 (URL → localStorage → 'vi'):
 *
 *   Priority 1: URL ?lang= or ?locale= (with 'vn' alias → 'vi')
 *   Priority 2: localStorage['mstem.locale']
 *   Priority 3: Hardcoded 'vi'
 *
 * See .planning/phases/01-foundation-branding-i18n-vn/01-RESEARCH.md §"FND-03 sub-B".
 */

import queryString from 'query-string';

const LOCALE_STORAGE_KEY = 'mstem.locale';
const DEFAULT_LOCALE = 'vi';

// Map non-ISO aliases to ISO 639-1 canonical codes.
// 'vn' is the ISO 3166-1 country code for Vietnam, frequently confused with
// the language code 'vi'. Accept both for user convenience.
const LOCALE_ALIAS = {vn: 'vi'};

const canonical = code => {
    if (typeof code !== 'string') return code;
    const lower = code.toLowerCase();
    return LOCALE_ALIAS[lower] || lower;
};

/**
 * Detect the locale to load on app boot.
 * @param {Array.<string>} supportedLocales - allowlist of locale codes the app supports
 * @return {string} one of supportedLocales, or DEFAULT_LOCALE if no match found
 */
const detectLocale = supportedLocales => {
    // 1. URL param: ?lang= or ?locale=
    const queryParams = queryString.parse(location.search);
    const candidates = [].concat(queryParams.locale, queryParams.lang)
        .filter(v => typeof v === 'string');
    for (let i = 0; i < candidates.length; i++) {
        const c = canonical(candidates[i]);
        if (supportedLocales.indexOf(c) !== -1) {
            return c;
        }
    }

    // 2. localStorage (wrapped to survive private-browsing and SSR).
    try {
        const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
        if (stored) {
            const c = canonical(stored);
            if (supportedLocales.indexOf(c) !== -1) {
                return c;
            }
        }
    } catch (e) {
        // localStorage may be disabled (private browsing) or missing (SSR/jsdom).
        // Fall through to default. Do not log — boot-time non-error.
    }

    // 3. Hardcoded default per D-11 (no browser-language auto-detect).
    return DEFAULT_LOCALE;
};

export {
    detectLocale as default,
    detectLocale,
    LOCALE_STORAGE_KEY
};
